import React, { useState, FormEvent, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, hasAdminPanelAccess } from '../../contexts/AuthContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { useMessageNotifications } from '../../contexts/MessageNotificationContext';
import { useIsMobile } from '../../hooks/useMediaQuery';
import Button from '../Button';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './Navbar.css';
import AppDropdown from 'components/AppDropdown';
import HamburgerMenu from './HamburgerMenu';
import MobileMenuSection from './MobileMenuSection';
import ProfilePicture from 'components/Profile/ProfilePicture';
import MessageCenterDropdown from './MessageCenterDropdown';
import ThemeToggle from '../theme/ThemeToggle';
import BrandLogo from '../BrandLogo';
import { formatRelativeTime } from '../../utils/formatRelativeTime';

/** Default avatar image when user has no profile picture (user silhouette SVG). */
const DEFAULT_AVATAR_DATA_URI =
  'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="%23adb5bd"%3E%3Cpath d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/%3E%3C/svg%3E';

const SEARCH_PLACEHOLDER = 'Dizüstü, masaüstü, ekran kartı, parça ara...';

function UnreadDot() {
  return <span className="sb-header-unread-dot sb-header__dot sb-header__dot--danger" aria-hidden="true" />;
}

const Navbar: React.FC = () => {
  const navigate = useNavigate();
  const { logout, user } = useAuth();
  const { unreadCount } = useNotifications();
  const { messageUnreadCount, conversationSummaries } = useMessageNotifications();
  const isMobile = useIsMobile();
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchDialogOpen, setSearchDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const searchDialogInputRef = useRef<HTMLInputElement>(null);

  const closeMenu = () => setMenuOpen(false);

  const openSearchDialog = () => {
    setSearchDialogOpen(true);
  };

  const closeSearchDialog = () => {
    setSearchDialogOpen(false);
  };

  const submitSearch = (query: string) => {
    const trimmed = query.trim();
    if (!trimmed) return;
    closeSearchDialog();
    setSearchQuery('');
    navigate(`/?q=${encodeURIComponent(trimmed)}`);
  };

  const handleSearchSubmit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    submitSearch(searchQuery);
  };

  useEffect(() => {
    if (searchDialogOpen) {
      searchDialogInputRef.current?.focus();
    }
  }, [searchDialogOpen]);

  const postListingButton = (
    <Link className="btn btn-primary btn-sm sb-header__cta" to="/ilan-ver" title="İlan Ver">
      <i className="fas fa-plus" aria-hidden="true" />
      <i className="fas fa-bullhorn" aria-hidden="true" />
      <span>İlan Ver</span>
    </Link>
  );

  const desktopNav = user ? (
    <div className="sb-header__actions">
      <ThemeToggle className="sb-header__icon-btn" />
      <Link
        to="/notifications"
        className="sb-header__icon-btn"
        aria-label={unreadCount > 0 ? 'Bildirimler (yeni)' : 'Bildirimler'}
        title="Bildirimler"
      >
        <i className="far fa-bell" aria-hidden="true" />
        {unreadCount > 0 && <UnreadDot />}
      </Link>
      <MessageCenterDropdown compact />
      <span className="sb-header__divider" aria-hidden="true" />
      {postListingButton}
      <AppDropdown
        id="userDropdown"
        imgSrc={user?.profilePictureUrl ?? DEFAULT_AVATAR_DATA_URI}
        name={user?.name || 'Ahmet Yılmaz'}
      >
        <>
          <li className="px-3 py-3 border-bottom mb-2">
            <div className="d-flex align-items-center">
              <ProfilePicture width={50} height={50} initialsFrom={user?.name} />
              <div className="ms-3">
                <h6 className="mb-1 fw-bold">{user?.name || 'Ahmet Yılmaz'}</h6>
                <p className="small text-muted mb-0">{user?.email || 'ahmet.yilmaz@example.com'}</p>
              </div>
            </div>
          </li>
          <li><Link className="dropdown-item py-2" to="/profil"><i className="fas fa-user me-2 text-primary"></i>Profilim</Link></li>
          <li><Link className="dropdown-item py-2" to="/ilanlarim"><i className="fas fa-list me-2 text-primary"></i>İlanlarım</Link></li>
          <li><Link className="dropdown-item py-2" to="/gonderilerim"><i className="fas fa-video me-2 text-primary"></i>Gönderilerim</Link></li>
          <li><Link className="dropdown-item py-2" to="/favoriler"><i className="fas fa-heart me-2 text-primary"></i>Favorilerim</Link></li>
          <li><Link className="dropdown-item py-2" to="/teknik-destek"><i className="fas fa-headset me-2 text-primary"></i>Destek Taleplerim</Link></li>
          {hasAdminPanelAccess(user) && (
            <li><Link className="dropdown-item py-2" to="/admin-panel"><i className="fas fa-shield-alt me-2 text-primary"></i>Admin Panel</Link></li>
          )}
          <li className="border-top mt-2">
            <button
              type="button"
              className="dropdown-item py-2 mt-2"
              onClick={logout}
            >
              <i className="fas fa-sign-out-alt me-2 text-danger"></i>Çıkış Yap
            </button>
          </li>
        </>
      </AppDropdown>
    </div>
  ) : (
    <div className="sb-header__actions">
      <ThemeToggle className="sb-header__icon-btn" />
      <span className="sb-header__divider" aria-hidden="true" />
      <Link className="btn btn-soft-primary btn-sm sb-header__cta" to="/giris" title="Giriş Yap">
        <i className="far fa-user" aria-hidden="true" />
        <span>Giriş Yap</span>
      </Link>
      {postListingButton}
    </div>
  );

  const mobileMenuContent = user ? (
    <>
      <MobileMenuSection title={<><i className="fas fa-user me-2 text-primary" />{user?.name || 'Hesabım'}</>}>
        <Link to="/profil" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-user me-2 text-primary"></i>Profilim</Link>
        <Link to="/ilanlarim" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-list me-2 text-primary"></i>İlanlarım</Link>
        <Link to="/gonderilerim" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-video me-2 text-primary"></i>Gönderilerim</Link>
        <Link to="/favoriler" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-heart me-2 text-primary"></i>Favorilerim</Link>
        <Link to="/teknik-destek" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-headset me-2 text-primary"></i>Destek Taleplerim</Link>
        {hasAdminPanelAccess(user) && (
          <Link to="/admin-panel" className="dropdown-item py-2" onClick={closeMenu}><i className="fas fa-shield-alt me-2 text-primary"></i>Admin Panel</Link>
        )}
        <button
          type="button"
          className="dropdown-item py-2 mt-2 w-100 text-start border-0 bg-transparent"
          onClick={() => {
            logout();
            closeMenu();
          }}
        >
          <i className="fas fa-sign-out-alt me-2 text-danger"></i>Çıkış Yap
        </button>
      </MobileMenuSection>
      <MobileMenuSection
        title={
          <>
            <i className="fas fa-envelope me-2 text-primary" />
            Mesajlar
            {messageUnreadCount > 0 && (
              <span className="badge bg-danger rounded-pill ms-2" aria-hidden="true">
                {messageUnreadCount > 99 ? '99+' : messageUnreadCount}
              </span>
            )}
          </>
        }
      >
        <Link to="/mesajlar" className="dropdown-item py-2" onClick={closeMenu}>
          <i className="fas fa-envelope me-2 text-primary" />
          Tüm Mesajlar
        </Link>
        {conversationSummaries.slice(0, 3).map((s) => (
          <Link
            key={s.conversationId}
            to={`/mesajlar?conversationId=${s.conversationId}`}
            className="dropdown-item py-2 d-flex flex-column"
            onClick={closeMenu}
          >
            <div className="d-flex justify-content-between align-items-center">
              <span className="fw-semibold small text-truncate" style={{ maxWidth: '70%' }}>{s.peerDisplayName}</span>
              <small className="text-muted">{formatRelativeTime(s.lastMessageAt)}</small>
            </div>
            <div className="d-flex justify-content-between align-items-center mt-1">
              <small className="text-muted text-truncate" style={{ maxWidth: '85%' }}>{s.lastMessagePreview ?? ''}</small>
              {s.unreadMessageCount > 0 && (
                <span className="badge bg-danger rounded-pill ms-1">{s.unreadMessageCount}</span>
              )}
            </div>
          </Link>
        ))}
        {conversationSummaries.length === 0 && (
          <div className="dropdown-item py-2 text-muted small">Henüz mesaj yok.</div>
        )}
      </MobileMenuSection>
      <Link to="/notifications" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom position-relative" onClick={closeMenu}>
        <i className="fas fa-bell me-2 text-primary" />
        Bildirimler
        {unreadCount > 0 && (
          <span className="sb-header-unread-dot rounded-circle bg-danger ms-2" style={{ width: '0.5rem', height: '0.5rem', display: 'inline-block' }} aria-hidden="true" />
        )}
      </Link>
      <Link to="/ilan-ver" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom" onClick={closeMenu}>
        <i className="fas fa-plus-circle me-2 text-primary" />
        İlan Ver
      </Link>
      <div className="d-flex align-items-center py-3 border-bottom">
        <ThemeToggle className="text-primary fs-5" showLabel />
      </div>
    </>
  ) : (
    <>
      <div className="d-flex align-items-center py-3 border-bottom">
        <ThemeToggle className="text-primary fs-5" showLabel />
      </div>
      <Link to="/ilan-ver" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom" onClick={closeMenu}>
        <i className="fas fa-plus-circle me-2 text-primary" />
        İlan Ver
      </Link>
      <Link to="/giris" className="d-flex align-items-center py-3 text-dark text-decoration-none border-bottom">
        <i className="fas fa-user me-2 text-primary" />
        Giriş Yap
      </Link>
    </>
  );

  return (
    <header className="header sb-header sticky-top">
      <nav className="sb-page-container sb-header__inner" aria-label="Ana menü">
        <Link className="sb-header__brand" to="/">
          <BrandLogo />
        </Link>

        {isMobile ? (
          <div className="sb-header__actions">
            <Button
              type="button"
              className="sb-header__icon-btn"
              onClick={openSearchDialog}
              aria-label="Arama"
            >
              <i className="fas fa-search" aria-hidden />
            </Button>
            <Button
              type="button"
              className="sb-header__icon-btn"
              onClick={() => setMenuOpen(true)}
              aria-label="Menüyü aç"
            >
              <i className="fas fa-bars" aria-hidden />
            </Button>
          </div>
        ) : (
          <>
            <form className="sb-header__search" role="search" onSubmit={handleSearchSubmit}>
              <button type="submit" className="sb-header__search-submit" aria-label="Ara">
                <i className="fas fa-search" aria-hidden />
              </button>
              <input
                type="search"
                className="form-control sb-input-fill sb-header__search-input"
                placeholder={SEARCH_PLACEHOLDER}
                aria-label="Arama"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </form>
            {desktopNav}
          </>
        )}
      </nav>

      {isMobile && (
        <HamburgerMenu isOpen={menuOpen} onClose={closeMenu}>
          {mobileMenuContent}
        </HamburgerMenu>
      )}

      {searchDialogOpen && (
        <>
          <div
            className="modal-backdrop fade show"
            onClick={closeSearchDialog}
            aria-hidden="true"
          />
          <div
            className="modal fade show d-block"
            tabIndex={-1}
            role="dialog"
            aria-modal="true"
            aria-labelledby="headerSearchDialogTitle"
          >
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title" id="headerSearchDialogTitle">
                    Arama
                  </h5>
                  <Button
                    type="button"
                    className="btn-close"
                    onClick={closeSearchDialog}
                    aria-label="Kapat"
                  />
                </div>
                <form onSubmit={handleSearchSubmit}>
                  <div className="modal-body">
                    <input
                      ref={searchDialogInputRef}
                      type="search"
                      className="form-control"
                      placeholder={SEARCH_PLACEHOLDER}
                      aria-label="Arama"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                    />
                  </div>
                  <div className="modal-footer">
                    <Button type="button" className="btn btn-secondary" onClick={closeSearchDialog}>
                      İptal
                    </Button>
                    <Button type="submit" className="btn btn-primary">
                      <i className="fas fa-search me-2" aria-hidden />
                      Ara
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Navbar;
